import type { FiltersState } from '../../hooks/useFilters';

type AvitoExperienceFields = Pick<FiltersState, 'avitoExperienceMin' | 'avitoExperienceMax'>;
type AvitoEducationLevel = NonNullable<FiltersState['avitoEducationLevel']>[number];
type AvitoSchedule = NonNullable<FiltersState['avitoSchedule']>[number];

interface ExperienceRange {
  min: number;
  max: number | null;
}

const HH_TOTAL_EXPERIENCE_TO_AVITO: Record<string, ExperienceRange> = {
  noExperience: { min: 0, max: 0 },
  between1And3: { min: 1, max: 3 },
  between3And6: { min: 3, max: 6 },
  moreThan6: { min: 6, max: null },
};

const HH_EDUCATION_TO_AVITO: Record<string, AvitoEducationLevel> = {
  secondary: 'secondary',
  special_secondary: 'special-secondary',
  unfinished_higher: 'unfinished-higher',
  higher: 'higher',
  bachelor: 'higher',
  master: 'higher',
  candidate: 'higher',
  doctor: 'higher',
};

const HH_WORK_SCHEDULE_EMPTY_VALUES = new Set(['5/2', '6/1', 'Другое']);
const HH_WORK_SCHEDULE_FLEXIBLE_VALUES = new Set(['Свободный', 'Гибкий график']);
const FULL_DAY_WORKING_HOURS = new Set(['8', '10', '12']);
const PARTIAL_DAY_WORKING_HOURS = new Set(['4', '6']);

const normalizeList = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.map(String).map((item) => item.trim()).filter(Boolean);
  }

  if (typeof value === 'string' && value.trim()) {
    return value.split(',').map((item) => item.trim()).filter(Boolean);
  }

  return [];
};

export function mapHhTotalExperienceToAvitoExperience(
  value: unknown,
): Partial<AvitoExperienceFields> | null {
  const values = Array.from(new Set(
    normalizeList(value)
      .filter((item) => HH_TOTAL_EXPERIENCE_TO_AVITO[item]),
  ));

  if (values.length === 0) {
    return null;
  }

  const ranges = values.map((item) => HH_TOTAL_EXPERIENCE_TO_AVITO[item]);
  const min = Math.min(...ranges.map((range) => range.min));
  const hasOpenMax = ranges.some((range) => range.max === null);
  const max = hasOpenMax
    ? ''
    : String(Math.max(...ranges.map((range) => range.max ?? range.min)));

  return {
    avitoExperienceMin: String(min),
    avitoExperienceMax: max,
  };
}

export function mapHhEducationToAvitoEducation(value: unknown): AvitoEducationLevel[] {
  return Array.from(new Set(
    normalizeList(value)
      .map((item) => HH_EDUCATION_TO_AVITO[item])
      .filter((item): item is AvitoEducationLevel => Boolean(item)),
  ));
}

export function mapHhWorkFormatToAvitoSchedule(value: unknown): AvitoSchedule[] {
  const values = normalizeList(value);
  return values.some((item) => item === 'Удаленка')
    ? ['remote']
    : [];
}

export function mapHhWorkScheduleToAvitoSchedule(value: unknown): AvitoSchedule[] {
  const mapped = normalizeList(value)
    .map((item): AvitoSchedule | '' => {
      if (HH_WORK_SCHEDULE_EMPTY_VALUES.has(item)) return '';
      if (HH_WORK_SCHEDULE_FLEXIBLE_VALUES.has(item)) return 'flexible';
      return 'shift';
    })
    .filter((item): item is AvitoSchedule => Boolean(item));

  return Array.from(new Set(mapped));
}

export function mapHhWorkingHoursToAvitoSchedule(value: unknown): AvitoSchedule[] {
  const mapped = normalizeList(value)
    .map((item): AvitoSchedule | '' => {
      const hours = item.match(/\d+/)?.[0] || '';
      if (FULL_DAY_WORKING_HOURS.has(hours)) return 'full-day';
      if (PARTIAL_DAY_WORKING_HOURS.has(hours)) return 'partial-day';
      return '';
    })
    .filter((item): item is AvitoSchedule => Boolean(item));

  return Array.from(new Set(mapped));
}

export function mergeAvitoScheduleMappings(...schedules: AvitoSchedule[][]): AvitoSchedule[] {
  return Array.from(new Set(schedules.flat()));
}
